import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Server, Hash, Copy } from "lucide-react";
import { toast } from "sonner";
import { validatorsApi } from "../lib/ledgerApi";

export default function ValidatorDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [vd, h] = await Promise.all([
          validatorsApi.get(id),
          validatorsApi.blocks(id, 50),
        ]);
        if (alive) { setV(vd); setHistory(h); }
      } catch {}
    };
    tick();
    const i = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(i); };
  }, [id]);

  if (!v) return <div className="px-6 py-10 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="validator-detail">
      <Link to="/validators" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All validators
      </Link>

      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          / Validator
        </div>
        <h1 className="text-3xl tracking-tight font-medium flex items-center gap-3">
          <Server className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          {v.name}
        </h1>
      </div>

      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
        <KV label="Status" value={v.status} />
        <KV label="Weight" value={v.weight} />
        <KV label="Blocks signed" value={v.blocks_signed_count ?? 0} />
        <KV label="Blocks missed" value={v.blocks_missed_count ?? 0} />
        <KV label="Blocks proposed" value={v.blocks_proposed_count ?? 0} />
        <KV label="Created" value={v.created_at?.slice(0, 10)} />
      </div>

      <div className="border border-border rounded-sm bg-card p-4 mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Public key</div>
        <button
          onClick={() => { navigator.clipboard.writeText(v.pubkey); toast.success("Pubkey copied"); }}
          className="font-mono text-xs break-all text-left inline-flex items-center gap-2 hover:underline"
          data-testid="validator-pubkey"
        >
          {v.pubkey}
          <Copy className="w-3 h-3 shrink-0" strokeWidth={1.5} />
        </button>
      </div>

      <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="validator-history">
        <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Recent signing activity
        </div>
        {history.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground">No blocks yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {history.map((b) => (
              <Link
                key={b.block_number}
                to={`/blocks/${b.block_number}`}
                className="block px-4 py-2 grid grid-cols-12 gap-2 text-xs hover:bg-muted/50"
              >
                <div className="col-span-2 font-mono">#{b.block_number}</div>
                <div className="col-span-5 font-mono text-[11px] text-muted-foreground truncate">
                  {b.block_hash.slice(0, 28)}…
                </div>
                <div className="col-span-3 font-mono text-[10px]">
                  {new Date(b.timestamp).toLocaleTimeString()}
                </div>
                <div className="col-span-2 text-right">
                  {b.was_proposer && (
                    <span className="px-1 py-0.5 border border-foreground/30 bg-foreground/5 rounded-sm text-[9px] uppercase tracking-widest mr-1">
                      proposer
                    </span>
                  )}
                  {b.byzantine_flag ? (
                    <span className="px-1 py-0.5 border border-status-tampered/40 bg-status-tampered/10 text-status-tampered rounded-sm text-[9px] uppercase tracking-widest">byz</span>
                  ) : b.valid_signature ? (
                    <span className="px-1 py-0.5 border border-status-ok/40 bg-status-ok/10 text-status-ok rounded-sm text-[9px] uppercase tracking-widest">signed</span>
                  ) : (
                    <span className="px-1 py-0.5 border border-muted-foreground/30 bg-muted text-muted-foreground rounded-sm text-[9px] uppercase tracking-widest">missed</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="border border-border rounded-sm bg-card px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div className="break-all">{value || "—"}</div>
    </div>
  );
}
